/**
 * Life Integration Service
 * Helps the wellness rep become part of daily routine, decision-making, and life
 */

import { supabase } from '../config/supabase';
import { deepPersonalizationService } from './deepPersonalizationService';

class LifeIntegrationService {
  constructor() {
    this.userPreferences = new Map();
    this.routineCache = new Map();
  }

  /**
   * Morning Routine Integration
   * Personalized morning check-ins and motivation
   */
  async generateMorningRoutine(userId) {
    try {
      const memory = await deepPersonalizationService.buildComprehensiveMemory(userId);
      
      return {
        greeting: await this.createPersonalizedGreeting(memory, userId),
        dailyIntention: this.suggestDailyIntention(memory),
        priorityReminder: this.remindOfPriorities(memory),
        encouragement: await this.providePersonalizedEncouragement(memory, userId),
        actionableStep: this.suggestDailyAction(memory),
        type: 'morning_routine'
      };
    } catch (error) {
      console.error('Error generating morning routine:', error);
      return this.getDefaultMorningRoutine();
    }
  }

  /**
   * Decision-Making Support
   * Help with daily decisions based on their values and goals
   */
  async provideDecisionSupport(userId, decision, context) {
    try {
      const memory = await deepPersonalizationService.buildComprehensiveMemory(userId);
      
      return {
        valuesAlignment: this.assessValuesAlignment(decision, memory.personalIdentity.coreValues),
        goalsImpact: this.assessGoalsImpact(decision, memory.dreamsAndGoals),
        personalizedAdvice: this.generatePersonalizedAdvice(decision, context, memory),
        considerationPoints: this.generateConsiderationPoints(decision, memory),
        supportReminder: this.createSupportReminder(memory),
        type: 'decision_support'
      };
    } catch (error) {
      console.error('Error providing decision support:', error);
      return this.getDefaultDecisionSupport(decision);
    }
  }

  /**
   * Evening Reflection Integration
   * End-of-day processing and growth
   */
  async generateEveningReflection(userId) {
    try {
      const memory = await deepPersonalizationService.buildComprehensiveMemory(userId);
      
      return {
        dayReflection: this.createDayReflectionPrompt(memory),
        gratitudePractice: this.suggestGratitudePractice(memory),
        progressAcknowledgment: this.acknowledgeProgress(memory),
        tomorrowPreparation: this.prepareTomorrow(memory),
        emotionalCheck: this.createEmotionalCheck(memory),
        type: 'evening_reflection'
      };
    } catch (error) {
      console.error('Error generating evening reflection:', error);
      return this.getDefaultEveningReflection();
    }
  }

  /**
   * Journaling Enhancement
   * Personalized journal prompts and insights
   */
  async enhanceJournaling(userId, journalEntry = null) {
    try {
      const memory = await deepPersonalizationService.buildComprehensiveMemory(userId);
      
      if (journalEntry) {
        // Respond to an existing journal entry
        return this.respondToJournalEntry(journalEntry, memory);
      } else {
        // Suggest journaling prompts
        return this.suggestJournalingPrompts(memory);
      }
    } catch (error) {
      console.error('Error enhancing journaling:', error);
      return this.getDefaultJournalingSupport();
    }
  }

  /**
   * Crisis Support Integration
   * 24/7 personalized crisis support
   */
  async provideCrisisSupport(userId, crisis) {
    try {
      const memory = await deepPersonalizationService.buildComprehensiveMemory(userId);
      
      return {
        personalizedComfort: this.createPersonalizedComfort(crisis, memory),
        copingReminder: this.remindOfCopingStrategies(memory),
        supportNetwork: this.activateSupportNetwork(memory),
        immediateActions: this.suggestImmediateActions(crisis, memory),
        professionalResources: this.provideProfessionalResources(),
        followUpPlan: this.createFollowUpPlan(memory),
        type: 'crisis_support'
      };
    } catch (error) {
      console.error('Error providing crisis support:', error);
      return this.getDefaultCrisisSupport();
    }
  }

  /**
   * Life Transition Support
   * Help during major life changes
   */
  async supportLifeTransition(userId, transition) {
    try {
      const memory = await deepPersonalizationService.buildComprehensiveMemory(userId);
      
      return {
        transitionValidation: this.validateTransition(transition, memory),
        historicalStrength: this.remindOfPastStrength(memory),
        adaptationStrategy: this.suggestAdaptationStrategy(transition, memory),
        supportActivation: this.activateSupport(memory),
        meaningMaking: this.helpFindMeaning(transition, memory),
        nextSteps: this.suggestNextSteps(transition, memory),
        type: 'transition_support'
      };
    } catch (error) {
      console.error('Error supporting life transition:', error);
      return this.getDefaultTransitionSupport(transition);
    }
  }

  // Implementation methods

  async createPersonalizedGreeting(memory, userId) {
    // Try to get personalized plan data as fallback if memory is limited
    let planData = null;
    const hasLimitedMemory = !memory || !memory.personalIdentity?.name || 
                            (!memory.highlowMoments?.highMoments?.length && 
                             !memory.dreamsAndGoals?.shortTermGoals?.length);
    
    if (hasLimitedMemory) {
      planData = await this.getPersonalizedPlanFallback(userId);
      console.log('📋 Using personalized plan data for greeting fallback:', planData);
    }
    
    const name = memory?.personalIdentity?.name || planData?.preferences?.preferredName;
    const recentHigh = memory?.highlowMoments?.highMoments?.[0];
    const currentGoal = memory?.dreamsAndGoals?.shortTermGoals?.[0] || planData?.goals?.[0];
    const timeOfDay = this.getTimeOfDay();
    
    // Dynamic time-based greetings
    const timeGreetings = {
      morning: ['Good morning', 'Rise and shine', 'What a beautiful morning'],
      afternoon: ['Good afternoon', 'Hope your day is going well', 'Afternoon check-in'],
      evening: ['Good evening', 'How was your day', 'Evening reflection time']
    };
    
    const baseGreeting = this.getRandomFromArray(timeGreetings[timeOfDay]);
    let greeting = name ? `${baseGreeting}, ${name}!` : `${baseGreeting}!`;
    
    // Add personalized elements
    if (recentHigh) {
      const encouragements = [
        `I hope you're still feeling that amazing energy from ${recentHigh.description || 'your recent success'}`,
        `Building on that incredible momentum from ${recentHigh.description || 'your recent win'}`,
        `You're on such a positive path after ${recentHigh.description || 'that recent breakthrough'}`
      ];
      greeting += ` ${this.getRandomFromArray(encouragements)}.`;
    } else if (planData?.strengths?.length > 0) {
      // Use plan strengths as fallback
      const strength = this.getRandomFromArray(planData.strengths);
      const strengthFramings = [
        `Your strength in ${strength} is truly inspiring`,
        `I love how you bring ${strength} to everything you do`,
        `That ${strength} of yours continues to amaze me`
      ];
      greeting += ` ${this.getRandomFromArray(strengthFramings)}.`;
    }
    
    if (currentGoal) {
      const goalFramings = [
        `Today's another step toward ${currentGoal}`,
        `Let's keep moving toward ${currentGoal}`,
        `Ready to make progress on ${currentGoal}`,
        `${currentGoal} is getting closer every day`
      ];
      greeting += ` ${this.getRandomFromArray(goalFramings)}.`;
    } else if (planData?.wellnessAreas?.length > 0) {
      // Use wellness areas from plan as fallback
      const area = this.getRandomFromArray(planData.wellnessAreas);
      const areaFramings = [
        `Let's focus on nurturing your ${area} today`,
        `How can we support your ${area} journey today?`,
        `Ready to explore your ${area} wellness?`
      ];
      greeting += ` ${this.getRandomFromArray(areaFramings)}.`;
    } else {
      // Add general motivation if no specific goal
      const motivations = [
        'Ready to make today meaningful?',
        'What amazing things will you discover about yourself today?',
        'I believe something wonderful is waiting for you today.',
        'Today is full of possibilities for growth and joy.'
      ];
      greeting += ` ${this.getRandomFromArray(motivations)}`;
    }
    
    return greeting;
  }

  suggestDailyIntention(memory) {
    const coreValues = memory.personalIdentity.coreValues;
    const currentGoals = memory.dreamsAndGoals.shortTermGoals;
    const recentStruggles = memory.strugglesAndChallenges.recentChallenges;
    
    if (recentStruggles.length > 0) {
      return `Today, let's focus on gentle progress with ${recentStruggles[0]}. Small steps count.`;
    }
    
    if (currentGoals.length > 0) {
      return `Today's intention: Take one meaningful step toward ${currentGoals[0]}.`;
    }
    
    if (coreValues.length > 0) {
      return `Today, let's live by your value of ${coreValues[0]}. How can you honor that today?`;
    }
    
    return "Today's intention: Be present and kind to yourself.";
  }

  generatePersonalizedAdvice(decision, context, memory) {
    const values = memory.personalIdentity.coreValues;
    const goals = memory.dreamsAndGoals.lifeGoals;
    const pastExperiences = memory.lifeStory.keyLifeEvents;
    
    let advice = `When I think about this decision in the context of who you are and what matters to you...`;
    
    if (values.length > 0) {
      advice += ` Your core values of ${values.slice(0, 2).join(' and ')} suggest that `;
      advice += this.getValueBasedAdvice(decision, values[0]);
    }
    
    if (goals.length > 0) {
      advice += ` Considering your goal of ${goals[0]}, this decision could `;
      advice += this.getGoalBasedAdvice(decision, goals[0]);
    }
    
    return advice;
  }

  createDayReflectionPrompt(memory) {
    const name = memory.personalIdentity.name;
    const currentGoals = memory.dreamsAndGoals.shortTermGoals;
    
    let prompt = name ? `${name}, as we end today together...` : 'As we end today together...';
    
    prompt += ` What moments from today feel most significant to you?`;
    
    if (currentGoals.length > 0) {
      prompt += ` Did you move closer to ${currentGoals[0]} in any way?`;
    }
    
    prompt += ` What are you feeling grateful for right now?`;
    
    return prompt;
  }

  respondToJournalEntry(entry, memory) {
    const name = memory.personalIdentity.name;
    const communicationStyle = memory.communicationProfile.communicationStyle;
    const recentProgress = memory.growthAndProgress.recentProgress;
    
    let response = name ? `${name}, thank you for sharing this with me. ` : 'Thank you for sharing this with me. ';
    
    // Analyze the emotional content of the entry
    const emotion = this.detectEmotionInEntry(entry);
    
    if (emotion === 'positive') {
      response += `I can feel the positive energy in your words. `;
      if (recentProgress) {
        response += `This feels connected to the growth I've been witnessing in you. `;
      }
    } else if (emotion === 'struggle') {
      response += `I hear the difficulty in what you're experiencing. `;
      if (memory.emotionalJourney.copingMechanisms.length > 0) {
        response += `Remember the strength you've shown before with ${memory.emotionalJourney.copingMechanisms[0]}. `;
      }
    }
    
    // Personalized insight
    response += this.generatePersonalizedInsight(entry, memory);
    
    return response;
  }

  // Helper methods

  getValueBasedAdvice(decision, value) {
    const valueAdviceMap = {
      'honesty': 'being truthful to yourself and others would be important.',
      'family': 'considering the impact on your loved ones would be wise.',
      'growth': 'choosing the path that helps you develop further makes sense.',
      'stability': 'prioritizing security and steady progress aligns with who you are.',
      'creativity': 'selecting the option that allows for innovation and expression fits you.',
      'helping': 'choosing what enables you to support others resonates with your nature.'
    };
    
    return valueAdviceMap[value] || 'staying true to your authentic self is key.';
  }

  detectEmotionInEntry(entry) {
    const text = entry.toLowerCase();
    
    const positiveWords = ['happy', 'grateful', 'excited', 'proud', 'accomplished', 'joy', 'amazing', 'wonderful'];
    const struggleWords = ['difficult', 'hard', 'stressed', 'anxious', 'worried', 'overwhelmed', 'frustrated', 'sad'];
    
    const positiveCount = positiveWords.filter(word => text.includes(word)).length;
    const struggleCount = struggleWords.filter(word => text.includes(word)).length;
    
    if (positiveCount > struggleCount) return 'positive';
    if (struggleCount > positiveCount) return 'struggle';
    return 'neutral';
  }

  generatePersonalizedInsight(entry, memory) {
    // This would be enhanced with more sophisticated analysis
    const recentThemes = memory.lifeStory.recentThemes || [];
    const strengths = memory.personalIdentity.personalityTraits;
    
    if (recentThemes.length > 0) {
      return `This connects to the themes of ${recentThemes[0]} that we've been exploring together. `;
    }
    
    if (strengths.length > 0) {
      return `Your ${strengths[0]} nature really comes through in how you're processing this. `;
    }
    
    return `I see you continuing to grow and understand yourself better. `;
  }

  remindOfPriorities(memory) {
    const currentGoals = memory.dreamsAndGoals.shortTermGoals;
    const lifeGoals = memory.dreamsAndGoals.lifeGoals;
    const values = memory.personalIdentity.coreValues;
    
    if (currentGoals.length > 0) {
      return `Remember, you're working toward ${currentGoals[0]}. What can you do today to move closer?`;
    }
    
    if (lifeGoals.length > 0) {
      return `Keep your bigger picture in mind: ${lifeGoals[0]}. Every day is a step forward.`;
    }
    
    if (values.length > 0) {
      return `Stay connected to what matters most: ${values[0]}. Let this guide your choices today.`;
    }
    
    return "Focus on what truly matters to you today.";
  }

  async providePersonalizedEncouragement(memory, userId) {
    // Try to get personalized plan data as fallback if memory is limited
    let planData = null;
    const hasLimitedPersonalData = !memory || 
                                  (!memory.highlowMoments?.highMoments?.length && 
                                   !memory.personalIdentity?.strengths?.length && 
                                   !memory.strugglesAndChallenges?.recentChallenges?.length &&
                                   !memory.dreamsAndGoals?.shortTermGoals?.length);
    
    if (hasLimitedPersonalData && userId) {
      planData = await this.getPersonalizedPlanFallback(userId);
      console.log('📋 Using personalized plan data for encouragement fallback:', planData);
    }
    
    const achievements = memory?.highlowMoments?.highMoments || [];
    const strengths = memory?.personalIdentity?.strengths || planData?.strengths || [];
    const recentChallenges = memory?.strugglesAndChallenges?.recentChallenges || planData?.challenges || [];
    const currentGoals = memory?.dreamsAndGoals?.shortTermGoals || planData?.goals || [];
    
    // Create dynamic, rotating encouragements based on user data
    if (achievements.length > 0) {
      const achievementEncouragements = [
        `You've shown incredible strength before, like when ${achievements[0].description || 'you overcame that challenge'}. You have that same power today.`,
        `Remember how amazing you felt when ${achievements[0].description || 'you achieved that breakthrough'}? That strength is still inside you.`,
        `You're the person who ${achievements[0].description || 'creates positive change'}. Today is another opportunity to shine.`,
        `Your track record speaks for itself - ${achievements[0].description || 'you make things happen'}. Trust yourself today.`
      ];
      return this.getRandomFromArray(achievementEncouragements);
    }
    
    if (strengths.length > 0) {
      const strength = typeof strengths[0] === 'string' ? strengths[0] : strengths[0].description || 'inner strength';
      const strengthEncouragements = [
        `Your ${strength} has gotten you through so much. Trust in that strength today.`,
        `I love how your ${strength} lights up everything you touch. Bring that energy to today.`,
        `Your ${strength} is such a gift - not just to you, but to everyone around you.`,
        `When you lean into your ${strength}, magic happens. What will you create today?`
      ];
      return this.getRandomFromArray(strengthEncouragements);
    }
    
    if (recentChallenges.length > 0) {
      const challenge = typeof recentChallenges[0] === 'string' ? recentChallenges[0] : recentChallenges[0].description || 'this challenge';
      const challengeEncouragements = [
        `I know ${challenge} has been difficult, but you're handling it with such grace. Be patient with yourself.`,
        `The way you're navigating ${challenge} shows incredible resilience. You're stronger than you know.`,
        `${challenge} is tough, but I see how you're growing through it. That's true courage.`,
        `Every step you take with ${challenge} is building your wisdom and strength. You're becoming unstoppable.`
      ];
      return this.getRandomFromArray(challengeEncouragements);
    }
    
    if (currentGoals.length > 0) {
      const goal = typeof currentGoals[0] === 'string' ? currentGoals[0] : currentGoals[0].description || 'your aspirations';
      const goalEncouragements = [
        `Your commitment to ${goal} inspires me. You're already succeeding just by showing up.`,
        `${goal} is lucky to have someone like you working toward it. You've got this.`,
        `I can see ${goal} getting closer every day because of your dedication. Keep going.`,
        `The way you pursue ${goal} shows who you really are - someone who doesn't give up.`
      ];
      return this.getRandomFromArray(goalEncouragements);
    }
    
    // Enhanced general encouragements that build app loyalty and engagement
    const generalEncouragements = [
      "You are exactly where you need to be, and you have everything within you to handle today.",
      "I see so much strength and potential in you. Today is your day to let it shine.",
      "You're not just surviving - you're growing, evolving, and becoming more yourself every day.",
      "There's something beautiful about how you approach life. Trust that instinct today.",
      "You have this unique way of finding light in any situation. That's a superpower.",
      "I believe in you more than you know. Your next breakthrough is closer than you think.",
      "Every conversation we have shows me more of your incredible wisdom and heart.",
      "You're building something amazing - a life that truly reflects who you are.",
      "The growth I've witnessed in you is absolutely inspiring. Keep being amazing.",
      "You have this rare combination of strength and compassion that changes everything around you."
    ];
    
    return this.getRandomFromArray(generalEncouragements);
  }

  suggestDailyAction(memory) {
    const currentGoals = memory.dreamsAndGoals.shortTermGoals;
    const challenges = memory.strugglesAndChallenges.recentChallenges;
    const values = memory.personalIdentity.coreValues;
    
    if (challenges.length > 0) {
      return `Today, try one small thing to address ${challenges[0]}. Even 10 minutes can make a difference.`;
    }
    
    if (currentGoals.length > 0) {
      return `Take one concrete step toward ${currentGoals[0]} today. What's the smallest action you could take?`;
    }
    
    if (values.length > 0) {
      return `Do something today that honors your value of ${values[0]}. How can you live this value actively?`;
    }
    
    return "Do one thing today that makes you feel proud of yourself.";
  }

  // Helper methods for dynamic content generation
  
  getTimeOfDay() {
    const hour = new Date().getHours();
    if (hour < 12) return 'morning';
    if (hour < 17) return 'afternoon';
    return 'evening';
  }
  
  getRandomFromArray(array) {
    return array[Math.floor(Math.random() * array.length)];
  }

  // Get personalized plan data as fallback for greetings
  async getPersonalizedPlanFallback(userId) {
    try {
      console.log('🎯 Getting personalized plan data for fallback content...');
      
      // Try to get stored personalized plan from localStorage first
      const storedPlan = localStorage.getItem('personalizedPlan');
      if (storedPlan) {
        const plan = JSON.parse(storedPlan);
        console.log('📋 Found stored personalized plan:', plan);
        return {
          goals: plan.goals || [],
          strengths: plan.strengths || [],
          challenges: plan.challenges || [],
          wellnessAreas: plan.wellnessAreas || [],
          preferences: plan.preferences || {}
        };
      }

      // Fallback to API call if no stored plan
      const { supabase } = await import('../config/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.log('❌ No session for plan fallback');
        return null;
      }

      const response = await fetch(`${process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_BACKEND_URL 
        : 'http://localhost:3001'}/api/personalized-plan/get-plan`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('📋 Retrieved personalized plan from API:', data);
        return data.plan || null;
      }

      console.log('📋 No personalized plan available');
      return null;
    } catch (error) {
      console.error('Error getting personalized plan fallback:', error);
      return null;
    }
  }

  // Default fallback methods

  getDefaultMorningRoutine() {
    const timeOfDay = this.getTimeOfDay();
    
    // Dynamic default greetings that still feel personal
    const greetings = {
      morning: [
        'Good morning! Ready to make today extraordinary?',
        'Rise and shine! Today is full of possibilities.',
        'What a beautiful morning to grow and discover new things about yourself.',
        'Good morning! I have a feeling today is going to be special for you.'
      ],
      afternoon: [
        'Good afternoon! How has your day been treating you?',
        'Afternoon check-in! Ready to make the rest of your day amazing?',
        'Hope your afternoon is going wonderfully!',
        'Good afternoon! What beautiful moments has today brought you?'
      ],
      evening: [
        'Good evening! Time to reflect on all the good you\'ve created today.',
        'Evening wisdom time! How are you feeling about your day?',
        'Good evening! Ready to wind down and appreciate your journey?',
        'What a day it\'s been! How are you taking care of yourself tonight?'
      ]
    };
    
    const intentions = [
      'Focus on progress, not perfection.',
      'Trust the process and trust yourself.',
      'Every small step is still a step forward.',
      'You\'re exactly where you need to be right now.',
      'Growth happens in the quiet moments too.',
      'Your journey is unique and valuable.'
    ];
    
    const encouragements = [
      'You have everything within you to handle whatever comes your way.',
      'I believe in your strength and your wisdom.',
      'You\'re braver than you believe and stronger than you know.',
      'Your next breakthrough is closer than you think.',
      'There\'s something beautiful about how you approach life.',
      'You\'re not just surviving - you\'re growing and becoming more yourself.'
    ];
    
    return {
      greeting: this.getRandomFromArray(greetings[timeOfDay]),
      dailyIntention: this.getRandomFromArray(intentions),
      priorityReminder: 'What matters most to you today?',
      encouragement: this.getRandomFromArray(encouragements),
      actionableStep: 'Take one small step toward something that matters to you.',
      type: 'morning_routine'
    };
  }

  getDefaultDecisionSupport(decision) {
    return {
      valuesAlignment: 'Consider what matters most to you.',
      goalsImpact: 'Think about your long-term aspirations.',
      personalizedAdvice: 'Trust your intuition and make the choice that feels authentic to you.',
      considerationPoints: ['What are the potential outcomes?', 'How does this align with your values?', 'What would you advise a friend in this situation?'],
      supportReminder: 'Remember, I\'m here to support you through whatever you decide.',
      type: 'decision_support'
    };
  }

  getDefaultEveningReflection() {
    return {
      dayReflection: 'What moments from today feel most significant?',
      gratitudePractice: 'What are three things you\'re grateful for today?',
      progressAcknowledgment: 'What progress did you make, however small?',
      tomorrowPreparation: 'What intention would you like to set for tomorrow?',
      emotionalCheck: 'How are you feeling as you end this day?',
      type: 'evening_reflection'
    };
  }
}

// Create singleton instance
const lifeIntegrationService = new LifeIntegrationService();

export { lifeIntegrationService };
export default LifeIntegrationService;
